import { useCallback, useEffect, useState } from 'react';
import { Modal } from '@telegram-apps/telegram-ui';
import { get } from '../../helpers/api';
import { firstChineseChar } from '../../helpers/firstChineseChar';
import styles from './ProxyRecordsModal.module.css';

type ProxyCdkRecord = {
  kind: 'activate' | 'recycle';
  id: number;
  gameName: string;
  gameId: string;
  cdk: string;
  steamId: string;
  price: number;
  time: string;
};

const PERIOD_LABEL: Record<number, string> = {
  1: '今日',
  2: '昨日',
  3: '近七天',
  4: '本月',
  5: '上月',
};

const AVATAR_COLORS = ['#1677ff', '#7257e8', '#ff9d27', '#e5484d', '#20a9b5'];

const colorOfName = (name: string) => {
  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = hash * 31 + name.charCodeAt(i);
  }
  const index = Math.abs(hash) % AVATAR_COLORS.length;
  return AVATAR_COLORS[index];
};

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  proxyId: number;
  proxyName: string;
  period: number;
  kind?: 'activate' | 'recycle';
  nested?: boolean;
};

export const ProxyRecordsModal = ({
  open,
  onOpenChange,
  proxyId,
  proxyName,
  period,
  kind,
  nested,
}: Props) => {
  const [records, setRecords] = useState<ProxyCdkRecord[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchRecords = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await get<ProxyCdkRecord[]>(
        `/admin/tel/proxy/records?proxyId=${proxyId}&period=${period}`
      );
      setRecords(Array.isArray(data) ? data : []);
    } catch (e) {
      setError(e instanceof Error ? e.message : '加载失败');
    } finally {
      setLoading(false);
    }
  }, [proxyId, period]);

  useEffect(() => {
    if (open) {
      fetchRecords();
    }
  }, [open, fetchRecords]);

  const label = PERIOD_LABEL[period] ?? '';
  const kindLabel =
    kind === 'activate' ? '激活记录' : kind === 'recycle' ? '回收记录' : '激活 / 回收记录';
  const visibleRecords = kind ? records.filter((record) => record.kind === kind) : records;

  return (
    <Modal
      nested={nested}
      open={open}
      onOpenChange={onOpenChange}
      className="app-modal"
      header={
        <div className={styles.banner}>
          <div className={styles.bannerTitle}>
            <span className={styles.bannerAvatar}>
              {firstChineseChar(proxyName || '')}
            </span>
            <span className={styles.bannerName}>
              {proxyName || `代理 #${proxyId}`}
            </span>
          </div>
          <div className={styles.bannerSub}>
            {label}{kindLabel}，共 {visibleRecords.length} 条
          </div>
          <div className={styles.bannerBars} aria-hidden="true">
            <i /><i /><i />
          </div>
        </div>
      }
    >
      <div className={styles.body}>
        {loading ? (
          <div className={styles.loading}>
            <span className="spinner" />
          </div>
        ) : error ? (
          <div className="error">{error}</div>
        ) : visibleRecords.length > 0 ? (
          <div className={styles.list}>
            {visibleRecords.map((record) => {
              const name = record.gameName || `游戏 #${record.gameId}`;
              const isRecycle = record.kind === 'recycle';
              return (
                <div key={`${record.kind}-${record.id}`} className={styles.item}>
                  <span
                    className={styles.avatar}
                    style={{ background: colorOfName(name) }}
                  >
                    {firstChineseChar(name)}
                  </span>
                  <div className={styles.itemMain}>
                    <div className={styles.itemTop}>
                      <span className={styles.name}>{name}</span>
                      <span className={styles.time}>{record.time}</span>
                    </div>
                    <div className={styles.itemBottom}>
                      <span className={`${styles.kindTag} ${isRecycle ? styles.kindRecycle : styles.kindActivate}`}>
                        {isRecycle ? '回收' : '激活'}
                      </span>
                      <span className={styles.cdk}>{record.cdk}</span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className={styles.empty}>暂无记录</div>
        )}
      </div>
    </Modal>
  );
};
